import React from "react";
import { Button, Tag, Text } from "@flixbus/honeycomb-react";
import {
  Icon,
  IconDocumentSolid,
  IconClose,
} from "@flixbus/honeycomb-icons-react";
import { toRejectCopy } from "./chatCopy";
import styles from "./ChatComposer.module.scss";

/** A file shown in the composer while it uploads. Carries live upload status so
 *  rejected / failed files surface a reason, unlike the plain name-only chip in a
 *  submitted user bubble. */
export interface ComposerFile {
  id: string;
  name: string;
  status: "uploading" | "admitted" | "rejected" | "error" | "pending";
  rejectionReason: string | null;
}

export interface ComposerChipsProps {
  files: ComposerFile[];
  disabled: boolean;
  onRemoveFile: (id: string) => void;
}

const ComposerChips: React.FC<ComposerChipsProps> = ({
  files,
  disabled,
  onRemoveFile,
}) => (
  <ul className={styles.composerChips}>
    {files.map((f) => (
      <li key={f.id} className={styles.composerChip}>
        <Icon InlineIcon={IconDocumentSolid} aria-hidden="true" />
        <Text Elem="span" small extraClasses={styles.composerChipName}>
          {f.name}
        </Text>
        {f.status === "uploading" && (
          <Tag appearance="neutral" display="subtle" small>
            Uploading…
          </Tag>
        )}
        {f.status === "rejected" && (
          <Tag appearance="danger" display="subtle" small>
            {toRejectCopy(f.rejectionReason)}
          </Tag>
        )}
        {f.status === "error" && (
          <Tag appearance="danger" display="subtle" small>
            Upload failed
          </Tag>
        )}
        <Button
          appearance="link"
          Elem="button"
          size="sm"
          display="square"
          disabled={disabled}
          onClick={() => onRemoveFile(f.id)}
          aria-label={`Remove ${f.name}`}
        >
          <Icon InlineIcon={IconClose} aria-hidden="true" />
        </Button>
      </li>
    ))}
  </ul>
);

export default React.memo(ComposerChips);
